import { FromNow } from '@plato/app-shared/components/from-now';
import { CrmTaskStatus } from '@plato/data/prisma';
import { SearchEntity, SearchEntityRoute } from '@plato/search/search-types';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Checkbox } from '@plato/ui-legacy/components/ui/checkbox';
import SmallSpinner from '@plato/ui-legacy/components/ui/small-spinner';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { cn } from '@plato/ui/util/classes';
import { join } from 'lodash-es';
import { PaperclipIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import { TaskSortBy } from '../../../server/dto/crm-task-dto.ts';
import { getFullName } from '../../../utils/name.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { useSearchEntityNavigation } from '../../shared/hooks/use-search-entity.ts';
import { TaskDueStatusText } from '../components/task-due-status-text.tsx';
import { TaskOptionsDropdown } from '../components/task-overview-table/task-options-dropdown.tsx';
import type { TaskViewGroup } from '../types/task-group.ts';
import { useToggleTask } from './use-toggle-task.tsx';

function RelatedToCell({ task }: { task: CrmTaskDTO }) {
    const { navigateToEntity } = useSearchEntityNavigation();
    const { t } = useTranslation();
    const getEntityTypeLabel = (entity: string): string => {
        switch (entity) {
            case SearchEntity.Customer:
                return t('customer');
            case SearchEntity.Product:
                return t('product');
            case SearchEntity.SalesOrder:
                return t('sales_order');
            case SearchEntity.SalesQuote:
                return t('sales_quote');
            default:
                return entity;
        }
    };

    const { links } = task;

    if (links.length === 0) {
        return null;
    }

    const customerLink = links.find((link) => link.modelType === SearchEntity.Customer);
    const salesOrderLink = links.find((link) =>
        ([SearchEntity.SalesOrder, SearchEntity.SalesQuote] as SearchEntity[]).includes(
            link.modelType as SearchEntity,
        ),
    );

    if (customerLink && salesOrderLink) {
        return (
            <IdAndNameCell
                onClick={() => {
                    navigateToEntity({
                        entity: salesOrderLink.modelType as SearchEntity,
                        erp_id: salesOrderLink.modelId,
                    });
                }}
                to={`${SearchEntityRoute[salesOrderLink.modelType as SearchEntity]}/${salesOrderLink.modelId}`}
                id={join([getEntityTypeLabel(salesOrderLink.modelType), salesOrderLink.title], ' ')}
                title={join([customerLink.modelId, customerLink.title], ' ')}
            />
        );
    }

    if (customerLink) {
        return (
            <IdAndNameCell
                onClick={() => {
                    navigateToEntity({
                        entity: customerLink.modelType as SearchEntity,
                        erp_id: customerLink.modelId,
                    });
                }}
                to={`${SearchEntityRoute[customerLink.modelType as SearchEntity]}/${customerLink.modelId}`}
                id={customerLink.modelId}
                title={customerLink.title}
            />
        );
    }
}

export type TaskColumnId =
    | TaskSortBy
    | 'select'
    | 'descriptionRich'
    | 'relatedTo'
    | 'attachmentsCount'
    | 'options';

export function useTaskOverviewColumns(group: TaskViewGroup): ColumnDef<TaskColumnId, CrmTaskDTO>[] {
    const { t } = useTranslation();
    const { openTaskDetails } = useDialogs();
    const { pendingTasks, setTaskComplete } = useToggleTask('task_overview_page');

    const columns = useMemo((): ColumnDef<TaskColumnId, CrmTaskDTO>[] => {
        return [
            {
                id: 'select',
                accessorKey: 'select',
                header: () => null,
                cell: ({ row: { original: task } }) => {
                    return pendingTasks.includes(task.taskId) ? (
                        <SmallSpinner />
                    ) : (
                        <Checkbox
                            checked={task.status === CrmTaskStatus.COMPLETED}
                            onCheckedChange={(value) => {
                                setTaskComplete(task.taskId, value === true);
                            }}
                            className="rounded-lg"
                        />
                    );
                },
                enableSorting: false,
                size: 1,
            },
            {
                id: 'descriptionRich',
                accessorKey: 'descriptionRich',
                header: t('tasks_overview_table__header__title', {
                    ns: 'tasks',
                }),
                meta: { aiChat: { label: t('tasks_overview_table__header__title', { ns: 'tasks' }) } },
                cell: (info) => (
                    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                     -- disabled due to legacy code */
                    <span
                        role="button"
                        className={cn(
                            'inline-block truncate h-5 whitespace-nowrap overflow-hidden text-ellipsis',
                            info.row.original.status === CrmTaskStatus.COMPLETED ? 'line-through' : '',
                        )}
                        style={{
                            maxWidth: 'calc(max(calc(100vw - 812px), 230px))',
                        }}
                        onClick={() => {
                            openTaskDetails(info.row.original.taskId);
                        }}
                    >
                        <RichTextViewer content={info.row.original.descriptionRich} />
                    </span>
                ),
                size: 240,
                maxSize: 240,
            },
            group === 'completed'
                ? {
                      id: TaskSortBy.COMPLETED_AT,
                      accessorKey: 'completedAt',
                      enableSorting: true,
                      meta: {
                          aiChat: {
                              label: t('tasks_overview_table__header__completed_at', { ns: 'tasks' }),
                          },
                      },
                      header: t('tasks_overview_table__header__completed_at', { ns: 'tasks' }),
                      cell: ({ row }) => (
                          <DoubleDeckerCell
                              align="left"
                              topClassName="text-neutral-500"
                              topLevel={<FromNow minimumUnit="minute">{row.original.completedAt}</FromNow>}
                              bottomLevel={<DateText>{row.original.completedAt}</DateText>}
                          />
                      ),
                  }
                : {
                      id: TaskSortBy.DUE_DATE,
                      accessorKey: 'dueDate',
                      enableSorting: true,
                      meta: {
                          aiChat: { label: t('tasks_overview_table__header__due_date', { ns: 'tasks' }) },
                      },
                      header: t('tasks_overview_table__header__due_date', { ns: 'tasks' }),
                      cell: ({ row }) => (
                          <DoubleDeckerCell
                              align="left"
                              topClassName="text-neutral-500"
                              topLevel={<TaskDueStatusText task={row.original} />}
                              bottomLevel={<DateText>{row.original.dueDate}</DateText>}
                          />
                      ),
                  },
            {
                accessorKey: 'relatedTo',
                accessorFn: (row) => row.links[0]?.title || '',
                header: t('tasks_overview_table__header__related_to', {
                    ns: 'tasks',
                }),
                meta: { aiChat: { label: t('tasks_overview_table__header__related_to', { ns: 'tasks' }) } },
                cell: ({ row }) => {
                    return <RelatedToCell task={row.original} />;
                },
            },
            {
                id: TaskSortBy.ASSIGNED_TO_USER,
                accessorKey: 'assignedTo',
                enableSorting: true,
                accessorFn: (row) => getFullName(row.assignedTo.firstName, row.assignedTo.lastName) || '',
                meta: {
                    aiChat: { label: t('tasks_overview_table__header__assigned_to_user', { ns: 'tasks' }) },
                },
                header: t('tasks_overview_table__header__assigned_to_user', {
                    ns: 'tasks',
                }),
            },
            {
                id: TaskSortBy.ASSIGNED_BY_USER,
                accessorKey: 'assignedBy',
                enableSorting: true,
                accessorFn: (row) => getFullName(row.assignedBy.firstName, row.assignedBy.lastName) || '',
                meta: {
                    aiChat: { label: t('tasks_overview_table__header__assigned_by_user', { ns: 'tasks' }) },
                },
                header: t('tasks_overview_table__header__assigned_by_user', {
                    ns: 'tasks',
                }),
            },
            {
                id: 'attachmentsCount',
                accessorKey: 'attachmentsCount',
                accessorFn: (row) => row.attachments.length,
                header: () => null,
                cell: ({ row }) => {
                    const count = row.original.attachments.length;
                    if (count === 0) {
                        return null;
                    }

                    return (
                        <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={PaperclipIcon}
                            onClick={() => {
                                openTaskDetails(row.original.taskId);
                            }}
                            aria-label={t('tasks_overview_table__attachments_count_aria', {
                                ns: 'tasks',
                                count,
                            })}
                        >
                            {count}
                        </Button>
                    );
                },
                enableSorting: false,
                size: 1,
            },
            {
                id: 'options',
                accessorKey: 'options',
                header: () => null,
                cell: ({ row }) => <TaskOptionsDropdown taskId={row.original.taskId} />,
                size: 1,
            },
        ];
    }, [t, openTaskDetails, pendingTasks, setTaskComplete, group]);

    return columns;
}
